import React from 'react'
import { COLUMN_TYPES, Icon, Semantic, constants } from '@adc/ui-morpheus'
import {
  ADD_FORM_EVENT_TEMPLATES_FILTER,
  ADD_FORM_VENUE_TEMPLATES_FILTER,
} from '../../constants/actionTypes'
const {
  actionCellActionTypes: { ACTION_CELL_ACTION_TYPES },
} = constants

const { Popup } = Semantic

const defaultColProps = {
  width: 150,
  isResizable: true,
  isReorderable: true,
  isVisible: true,
}

export const tableConfig = {
  fieldDefinitions: [
    {
      name: 'Venue Code',
      apiRef: 'venue_code',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColProps, width: 100 },
      },
    },
    {
      name: 'Venue Name',
      apiRef: 'venue_name',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColProps,
      },
    },
    {
      name: 'Venue Title',
      apiRef: 'venue_title',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColProps,
      },
    },
    {
      name: 'Auction Start',
      apiRef: 'venue_schedule.bid_start_date_time',
      formatOptions: {
        midnightAllowed: false,
      },
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: defaultColProps,
      },
    },
    {
      name: 'Auction End',
      apiRef: 'venue_schedule.initial_bidding_end_date_time',
      formatOptions: {
        midnightAllowed: false,
      },
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: defaultColProps,
      },
    },
    {
      name: 'Address',
      apiRef: 'full_address',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColProps, width: 300 },
      },
    },
    {
      name: 'Venue Type',
      apiRef: 'venue_type',
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColProps, width: 100 },
      },
    },
    {
      name: 'Trustee',
      apiRef: 'trustee_sale',
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: { ...defaultColProps, width: 100 },
      },
    },
    {
      name: 'Web Live Status',
      apiRef: 'venue_configuration.web_live_status',
      columnDefinition: {
        type: COLUMN_TYPES.STATUS,
        positiveValue: 'Active',
        negativeValue: 'Inactive',
        colProps: { ...defaultColProps, width: 100 },
      },
    },
    {
      name: '',
      apiRef: '__actions__',
      columnDefinition: {
        actions: [
          ACTION_CELL_ACTION_TYPES.DISPLAY_DETAILS,
          ACTION_CELL_ACTION_TYPES.REMOVE_ROW,
          ACTION_CELL_ACTION_TYPES.EDIT_DETAILS,
        ],
        type: COLUMN_TYPES.ACTION,
        colProps: { width: 75, fixedRight: true, align: 'center' },
      },
    },
  ],
}

const getFetchVenueTemplatesOptions = searchValue => [
  {
    fields: ['template_status'],
    values: ['ACTIVE'],
  },
  {
    fields: ['template_name'],
    partialMatchFields: ['template_name'],
    values: [searchValue],
  },
]

const getFetchEventTemplatesOptions = searchValue => [
  {
    fields: ['status'],
    values: ['ACTIVE'],
  },
  {
    fields: ['event_template_name'],
    partialMatchFields: ['event_template_name'],
    values: [searchValue],
  },
]

const editSchedule = (actionIndex, rowIndex, primaryId, data, onRowAction) => (
  <Popup
    content='Edit Schedule'
    key={actionIndex}
    size='tiny'
    trigger={
      <Icon
        link
        color='info'
        size='large'
        name='calendar'
        style={{ marginTop: -3 }}
        data-elm-id={`action_schedule_${rowIndex}`}
        onClick={() => {
          onRowAction(
            ACTION_CELL_ACTION_TYPES.EDIT_DETAILS,
            rowIndex,
            primaryId
          )
        }}
      />
    }
  />
)

const editVenue = (actionIndex, rowIndex, primaryId, data, onRowAction) =>
  !!primaryId && (
    <Popup
      content='Edit Venue'
      key={actionIndex}
      size='tiny'
      trigger={
        <Icon
          link
          color='info'
          size='large'
          name='write square'
          style={{ marginTop: -3 }}
          data-elm-id={`action_edit_${rowIndex}`}
          onClick={() => {
            onRowAction(
              ACTION_CELL_ACTION_TYPES.DISPLAY_DETAILS,
              rowIndex,
              primaryId
            )
          }}
        />
      }
    />
  )

const removeRow = (actionIndex, rowIndex, primaryId, data, onRowAction) =>
  !primaryId && (
    <Popup
      content='Remove Venue'
      key={actionIndex}
      size='tiny'
      trigger={
        <Icon
          link
          color='error'
          size='large'
          name='delete'
          style={{ marginTop: -3 }}
          data-elm-id={`action_remove_${rowIndex}`}
          onClick={() => {
            onRowAction(
              ACTION_CELL_ACTION_TYPES.REMOVE_ROW,
              rowIndex,
              primaryId
            )
          }}
        />
      }
    />
  )

const columnAction = actionCellActionType => {
  switch (actionCellActionType) {
    case ACTION_CELL_ACTION_TYPES.DISPLAY_DETAILS:
      return editVenue
    case ACTION_CELL_ACTION_TYPES.REMOVE_ROW:
      return removeRow
    case ACTION_CELL_ACTION_TYPES.EDIT_DETAILS:
      return editSchedule
  }
}

/**
 *
 * @param {*} fieldDefinitions
 * THIS SELECTOR IS TO MAINTAIN BACKWARDS COMPATIBILITY WITH EVERYTHING IN TOOLKIT
 * It will change/ be removed when everything there changes. This is so we can refactor slowly
 */
const columnSelector = state =>
  state.fieldDefinitions
    .filter(field => field.columnDefinition)
    .map(col => {
      const column = {
        ...col.columnDefinition,
        ...{ field: col.apiRef, title: col.name },
      }
      if (column.actions) column.actions = column.actions.map(columnAction)
      if (col.dataType) column.dataType = col.dataType
      if (col.searchType) column.searchType = col.searchType
      if (col.options) column.options = col.options
      if (col.formatOptions) column.formatOptions = col.formatOptions
      return column
    })

export default (state = tableConfig, action) => {
  switch (action.type) {
    case ADD_FORM_EVENT_TEMPLATES_FILTER:
      return {
        ...state,
        filterClauses: getFetchEventTemplatesOptions(action.searchValue),
      }
    case ADD_FORM_VENUE_TEMPLATES_FILTER:
      return {
        ...state,
        filterClauses: getFetchVenueTemplatesOptions(action.searchValue),
      }
  }
  return state
}

export { columnSelector }
